import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useReactToPrint } from "react-to-print";
import ReactPaginate from "react-paginate";
import MobileNav from "../mobileNav/MobileNav";
import PrintingHeader from "../printingHeader/PrintingHeader";
import printIcon from "../../assets/print-icon.svg";
import SearchInput from "../searchBar/SearchBar";
import "./maintainceDone.css";

const MaintainceDone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  // Filter states - Updated to use technicianId instead of technician name
  const [filters, setFilters] = useState({
    technicianId: "", // Changed from technician to technicianId
    city: "",
    MaintenanceStartDate: "",
    MaintenanceEndDate: "",
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const maxLength = 7;

  const itemsPerPageOptions = [25, 50, 100, 300, 400, totalItems];

  // Helper function to remove empty values from object
  const removeEmptyValues = (obj) => {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
      if (
        cleaned[key] === "" ||
        cleaned[key] === null ||
        cleaned[key] === undefined
      ) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Helper function to build search parameters
  const buildSearchParams = (term) => {
    if (!term.trim()) return {};

    // Check if the search term is a phone number (contains only digits, +, or spaces)
    const isPhoneNumber = /^[\d+\s]+$/.test(term);

    // Check if the search term is a name (contains Arabic/English letters)
    const isName = /[\u0600-\u06FFa-zA-Z]/.test(term);

    const searchParams = {};

    if (isPhoneNumber) {
      // If it looks like a phone number, search only by phone
      searchParams.PhoneNumber = term.replace(/\s/g, "");
    } else if (isName) {
      // If it looks like a name, search only by name
      searchParams.CustomerName = term;
    } else {
      // For mixed or unclear input, search by both but let backend handle it
      searchParams.CustomerName = term;
      searchParams.PhoneNumber = term;
    }

    return searchParams;
  };

  // Fetch data - Updated to accept filter parameters
  const getAllAppointments = async (
    currentPage = 1,
    pageSize = itemsPerPage,
    searchParams = {},
    filterParams = {}
  ) => {
    try {
      setLoading(true);
      const technicianId = localStorage.getItem("UserId");
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds =
        maintenanceAreaIdsString?.split(",").map(Number) || [];

      const url = `https://api.generalhome.sa/api/maintenance/GetFinishedRequests`;

      // Combine search and filter parameters and remove empty values
      const payload = removeEmptyValues({
        Page: currentPage,
        PageSize: pageSize,
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId,
        ...searchParams,
        ...filterParams, // Add filter parameters here
      });

      const res = await axios.post(url, payload);
      const totalItems = res.data.TotalItems;
      const totalPages = res.data.TotalPages || 1;
      const totalNumberOfPages = totalPages || Math.ceil(totalItems / pageSize);

      setPageCount(totalNumberOfPages);
      setDisplayData(res.data.Items);
      setTotalItems(res.data.TotalItems);
      setCurrentPage(currentPage - 1);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError(err.message);
      setDisplayData([]);
      setTotalItems(0);
      setPageCount(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  };

  // Function to get technicians
  const getApprovedTechnicians = async () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);
      const url = `https://api.generalhome.sa/api/maintenance/GetApprovedTechnicians`;
      const res = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
      });
      setTechnicians(res.data);
    } catch (err) {
      console.error("Error fetching technicians:", err);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  // Handle search submit (when user clicks search button or presses enter)
  const handleSearchSubmit = () => {
    const searchParams = buildSearchParams(searchTerm);
    getAllAppointments(1, itemsPerPage, searchParams, filters);
  };

  // Handle enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Reset to first page when applying filters
    getAllAppointments(1, itemsPerPage, buildSearchParams(searchTerm), filters);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      technicianId: "", // Reset to empty string
      city: "",
      MaintenanceStartDate: "",
      MaintenanceEndDate: "",
    });
    setSearchTerm(""); // Clear search term when resetting filters
    // Reset to first page and fetch without filters
    getAllAppointments(1, itemsPerPage);
  };

  // Pagination click
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    getAllAppointments(
      pageNumber,
      itemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    getAllAppointments(
      1,
      newItemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  // Print handler
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
  });

  const formatACTypes = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    return maintenanceDetails.map((detail) => detail.ACTypeName).join("، ");
  };

  useEffect(() => {
    getAllAppointments(1, itemsPerPage);
    getApprovedTechnicians();
  }, []);

  // Render pagination
  const renderPagination = () => (
    <ReactPaginate
      forcePage={currentPage}
      previousLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.94 4L6 4.94L9.05333 8L6 11.06L6.94 12L10.94 8L6.94 4Z"
            fill="black"
          />
        </svg>
      }
      nextLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.06 12L11 11.06L7.94667 8L11 4.94L10.06 4L6.06 8L10.06 12Z"
            fill="black"
            fillOpacity="0.3"
          />
        </svg>
      }
      breakLabel={"..."}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={3}
      onPageChange={handlePageClick}
      containerClassName={"pagination justify-content-end"}
      pageClassName={"page-item"}
      pageLinkClassName={"page-link"}
      previousClassName={"page-item"}
      previousLinkClassName={"page-link"}
      nextClassName={"page-item"}
      nextLinkClassName={"page-link"}
      breakClassName={"page-item"}
      breakLinkClassName={"break-link"}
      activeClassName={"active"}
    />
  );

  return (
    <>
      <MobileNav />
      <section className="newAppointment maintainceDone mt-0">
        <Helmet>
          <meta charSet="utf-8" />
          <title>الطلبات التي تم تنفيذها</title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/maintainceDone"
          />
        </Helmet>

        {loading ? (
          <div className="spinner-container">
            <ClipLoader loading={loading} size={100} color="#00b1ff" />
          </div>
        ) : (
          <div className="newAppointment-container" ref={componentRef}>
            <PrintingHeader
              title="الطلبات التي تم تنفيذها"
              count={totalItems}
            />
            <SearchInput
              searchTerm={searchTerm}
              handleSearch={handleSearchChange}
              handleSearchSubmit={handleSearchSubmit}
              handleKeyPress={handleKeyPress}
              setSearchTerm={setSearchTerm}
              placeholder="ابحث عن اسم العميل أو رقم الجوال"
            />

            <div className="d-flex info justify-content-between">
              <div className="info-header">
                <h2>الطلبات التي تم تنفيذها</h2>
                <h3>{totalItems}</h3>
              </div>
              <div className="print-icon-holder">
                <img
                  src={printIcon}
                  alt="printIcon"
                  className="print-icon"
                  onClick={handlePrint}
                />
              </div>
            </div>

            {/* Items per page selector and pagination */}
            <div className="paginateComponent">
              <div className="items-per-page-selector">
                <select
                  id="itemsPerPage"
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  style={{
                    width: "80px",
                    display: "inline-block",
                    margin: "0 10px",
                  }}
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {pageCount > 0 && renderPagination()}
            </div>

            {/* Filter form - UPDATED TO SEND TECHNICIAN ID */}
            <div className="form-holder">
              <h5>تصفية حسب</h5>
              <form className="filter-form" onSubmit={handleFilterSubmit}>
                <div className="filter-row">
                  <div className="filter-group">
                    <select
                      name="technicianId" // Changed to technicianId
                      value={filters.technicianId} // Changed to technicianId
                      onChange={handleFilterChange}
                    >
                      <option value="">اختر الفني</option>
                      {technicians.map((tech) => (
                        <option
                          key={tech.TechnicianId}
                          value={tech.TechnicianId}
                        >
                          {" "}
                          {/* Send ID as value */}
                          {tech.Username} {/* Display username */}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <input
                      type="text"
                      name="city"
                      placeholder="ابحث بالمدينة"
                      value={filters.city}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
                <div className="filter-row">
                  <div className="filter-group date-group">
                    <label>من تاريخ:</label>
                    <input
                      type="date"
                      name="MaintenanceStartDate"
                      value={filters.MaintenanceStartDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                  <div className="filter-group date-group">
                    <label>إلى تاريخ:</label>
                    <input
                      type="date"
                      name="MaintenanceEndDate"
                      value={filters.MaintenanceEndDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
                <div className="filter-button-container">
                  <button
                    type="button"
                    className="reset-button"
                    onClick={handleResetFilters}
                    disabled={loading}
                  >
                    إعادة تعيين
                  </button>
                  <button type="submit" disabled={loading}>
                    {loading ? "جاري التصفية..." : "تصفية"}
                  </button>
                </div>
              </form>
            </div>

            {/* Table */}
            <div className="table-responsive-sm overflow-auto">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>رقم الطلب</th>
                    <th>إسم العميل</th>
                    <th>رقم الهاتف</th>
                    <th>المدينه</th>
                    <th>نوع المكيف</th>
                    <th>تاريخ الصيانه</th>
                    <th>الفني المسؤول</th>
                    <th className="text-center">التحكم</th>
                  </tr>
                </thead>
                {displayData?.length > 0 ? (
                  <tbody>
                    {displayData.map((item) => (
                      <tr key={item.RequestId}>
                        <td>{item.FormattedRequestNumber}</td>
                        <td>
                          {item.CustomerName?.length > maxLength
                            ? `${item.CustomerName.substring(0, maxLength)}...`
                            : item.CustomerName}
                        </td>
                        <td>
                          <a
                            href={`https://api.whatsapp.com/send?phone=+966${item.PhoneNumber.replace(
                              /[^\d+]/g,
                              ""
                            )}`}
                            target="__blank"
                            rel="noopener noreferrer"
                          >
                            {item.PhoneNumber}
                          </a>
                        </td>
                        <td>{item.City}</td>
                        <td>
                          {formatACTypes(item.MaintenanceRequestDetails)
                            ?.length > maxLength
                            ? `${formatACTypes(
                                item.MaintenanceRequestDetails
                              ).substring(0, maxLength)}...`
                            : formatACTypes(item.MaintenanceRequestDetails)}
                        </td>
                        <td>{item.MaintenanceStartDate}</td>
                        <td>{item.TechnicianName}</td>
                        <td className="btn-box">
                          <Link to={`/dashboard/inProgress/${item.RequestId}`}>
                            <button>تفاصيل</button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr className="no-data-message">
                      <td colSpan="8">لا توجد بيانات لعرضها حاليا :(</td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default MaintainceDone;
